// The calculations behind the Futures Allocation report, kept free of UI and API
// imports so they can be unit tested on their own.
//
// How the trades flow for a customer like Diageo: futures are bought at a parent
// location (Canada) and budgeting is done for the parent, split evenly between its
// child locations (Gimli, Valleyfield). When a child takes delivery through an exchange
// for physical, the parent sells its futures and the child buys futures, sells them
// again and buys a physical. So the parent shows longs, the shorts that rolled volume
// down and the net still held; each child's futures net to zero and its physical
// volume stands against its share of the budget.
//
// Rows are futures contract months, not budget months. Corn budgeted for January and
// February is priced off the March contract, so the fiscal year's budget months are
// pushed through the commodity's contract month mapping and the budgets and physicals
// of every month that prices off the same contract are summed into that contract's row.
//
// Swaps are hedges too and are counted alongside futures. Some child locations trade
// their own futures and swaps instead of taking them through an exchange for physical;
// those are flagged in the report settings and their hedges are counted with the parent
// as well as on their own card.
import { BudgetModel, LocationModel, PositionModel, PositionType, Side } from '../../../../Generated/Commodity-Management-Api';
import { ContractMonthMappingByBudgetMonth, resolveContractDate } from '../../../../Helpers/ContractMonthMappingHelper';
import { FiscalMonth } from '../../../../Helpers/FiscalYearHelper';
import { numberToShortMonth } from '../../../../Helpers/MonthHelper';
import { getCurrentForecastForMonth } from './BudgetRoundHelpers';

export interface FuturesAllocationRow {
    path: string[];
    monthSortKey: number;
    locationName?: string;
    longFutures: number;
    shortFutures: number;
    netFutures: number;
    budgetVolume: number;
    physicalVolume: number;
}

type VolumeField = Exclude<keyof FuturesAllocationRow, 'path' | 'monthSortKey' | 'locationName'>;

const VOLUME_FIELDS: VolumeField[] = ['longFutures', 'shortFutures', 'netFutures', 'budgetVolume', 'physicalVolume'];

export function monthLabel({ year, month }: FiscalMonth): string {
    return `${numberToShortMonth(month)} ${String(year).slice(-2)}`;
}

export function monthSortKey({ year, month }: FiscalMonth): number {
    return year * 12 + month;
}

function sameMonth(a: { year?: number; month?: number } | undefined, b: FiscalMonth): boolean {
    return a?.year === b.year && a?.month === b.month;
}

function toFiscalMonth(date: { year?: number; month?: number } | undefined): FiscalMonth | undefined {
    return date?.year !== undefined && date?.month !== undefined ? { year: date.year, month: date.month } : undefined;
}

/** Futures and swaps are both hedges the report counts as "futures". */
export function isHedge(position: PositionModel): boolean {
    return position.positionType === PositionType.Future || position.positionType === PositionType.Swap;
}

function sumField(rows: FuturesAllocationRow[], field: VolumeField): number {
    return rows.reduce((acc, row) => acc + row[field], 0);
}

function sumRows(path: string[], monthSortKey: number, rows: FuturesAllocationRow[], locationName?: string): FuturesAllocationRow {
    const row = { path, monthSortKey, locationName } as FuturesAllocationRow;
    VOLUME_FIELDS.forEach(field => {
        row[field] = sumField(rows, field);
    });
    return row;
}

/** The contract month a budget month prices off, through the commodity's mapping. */
export function contractMonthForBudgetMonth(budgetMonth: FiscalMonth, mappings: ContractMonthMappingByBudgetMonth): FiscalMonth {
    return toFiscalMonth(resolveContractDate(budgetMonth.month, budgetMonth.year, mappings)) ?? budgetMonth;
}

/**
 * The contract months the report shows: every contract the fiscal year's budget months
 * price off, plus any contract the given futures were actually traded in, oldest first.
 */
export function contractMonthsFor(
    fiscalYearMonths: FiscalMonth[],
    mappings: ContractMonthMappingByBudgetMonth,
    futures: PositionModel[],
): FiscalMonth[] {
    const byKey = new Map<number, FiscalMonth>();
    const add = (month: FiscalMonth | undefined) => {
        if (month) byKey.set(monthSortKey(month), month);
    };

    fiscalYearMonths.forEach(budgetMonth => add(contractMonthForBudgetMonth(budgetMonth, mappings)));
    futures.filter(isHedge).forEach(p => add(toFiscalMonth(p.contractDate)));

    return Array.from(byKey.values()).sort((a, b) => monthSortKey(a) - monthSortKey(b));
}

/**
 * The earliest and latest budget date the report needs positions for: the fiscal year
 * itself, widened to any contract month that falls outside it. Futures sit on their
 * contract month until somebody assigns them, so a contract after the fiscal year's end
 * would otherwise never be fetched.
 */
export function positionDateRange(fiscalYearMonths: FiscalMonth[], mappings: ContractMonthMappingByBudgetMonth): { from: FiscalMonth; to: FiscalMonth } | undefined {
    if (fiscalYearMonths.length === 0) return undefined;

    const months = [...fiscalYearMonths, ...fiscalYearMonths.map(m => contractMonthForBudgetMonth(m, mappings))];
    const sorted = months.sort((a, b) => monthSortKey(a) - monthSortKey(b));
    return { from: sorted[0], to: sorted[sorted.length - 1] };
}

/**
 * Long, short and signed physical volume for one contract month. Hedges are counted at
 * `hedgeLocationIds` and physicals at `physicalLocationIds`; they differ for a parent
 * row that also counts the hedges of children flagged as trading their own.
 */
function volumesAt(
    positions: PositionModel[],
    hedgeLocationIds: number[],
    physicalLocationIds: number[],
    contractMonth: FiscalMonth,
    budgetMonths: FiscalMonth[],
    mappings: ContractMonthMappingByBudgetMonth,
) {
    const at = (locationIds: number[]) => positions.filter(p => locationIds.includes(p.location?.locationId));

    // A future or swap belongs to the contract it was traded in.
    const futures = at(hedgeLocationIds).filter(p => isHedge(p) && sameMonth(p.contractDate ?? p.budgetDate, contractMonth));
    const longFutures = futures.filter(p => p.side === Side.Buy).reduce((acc, p) => acc + (p.volume ?? 0), 0);
    const shortFutures = futures.filter(p => p.side === Side.Sell).reduce((acc, p) => acc + (p.volume ?? 0), 0);

    // A physical belongs to the contract its budget month prices off, and only budget
    // months inside the fiscal year count.
    const physicalVolume = at(physicalLocationIds)
        .filter(p => p.positionType === PositionType.Physical)
        .filter(p => budgetMonths.some(m => sameMonth(p.budgetDate, m)))
        .filter(p => sameMonth(contractMonthForBudgetMonth(toFiscalMonth(p.budgetDate)!, mappings), contractMonth))
        .reduce((acc, p) => acc + (p.volume ?? 0) * (p.side === Side.Sell ? -1 : 1), 0);

    return { longFutures, shortFutures, netFutures: longFutures - shortFutures, physicalVolume };
}

/**
 * The current forecast volume budgeted directly at a location across the given budget
 * months, or undefined when none of them is budgeted there.
 */
function ownBudget(budgets: BudgetModel[], locationId: number, budgetMonths: FiscalMonth[]): number | undefined {
    const atLocation = budgets.filter(b => b.location?.locationId === locationId);
    const volumes = budgetMonths
        .map(m => getCurrentForecastForMonth(atLocation, m.year, m.month)?.volume)
        .filter((v): v is number => v !== undefined && v !== null);

    return volumes.length === 0 ? undefined : volumes.reduce((acc, v) => acc + v, 0);
}

/**
 * The parent location's budget across the given budget months. Budgets are usually
 * entered against the child locations, so when nothing is budgeted at the parent
 * itself the children's budgets are summed back up.
 */
export function parentBudget(budgets: BudgetModel[], parentLocationId: number, childLocations: LocationModel[], budgetMonths: FiscalMonth[]): number {
    const own = ownBudget(budgets, parentLocationId, budgetMonths);
    if (own !== undefined) return own;

    return childLocations.reduce((acc, child) => acc + (ownBudget(budgets, child.locationId, budgetMonths) ?? 0), 0);
}

export interface FuturesAllocationInput {
    fiscalYearMonths: FiscalMonth[];
    /** The selected commodity's contract month mapping, keyed by budget month. */
    mappings: ContractMonthMappingByBudgetMonth;
    /** The location whose dashboard this is: where futures are bought. */
    parentLocation: LocationModel;
    childLocations: LocationModel[];
    /** Gross futures and physicals for the commodity, already in the display unit. */
    positions: PositionModel[];
    /** Budgets for the commodity, already in the display unit. */
    budgets: BudgetModel[];
    /**
     * Child locations that trade their own futures and swaps rather than taking them
     * from the parent through an exchange for physical. Their hedges are counted in the
     * parent's rows as well as in their own.
     */
    locationIdsWithoutEfp?: number[];
}

/** The children whose hedges are counted with the parent, limited to the children actually shown. */
export function rolledUpChildren(childLocations: LocationModel[], locationIdsWithoutEfp: number[] | undefined): LocationModel[] {
    const ids = locationIdsWithoutEfp ?? [];
    return childLocations.filter(child => ids.includes(child.locationId));
}

/** Each contract month the report shows, with the fiscal-year budget months that price off it. */
function contractMonthBuckets(input: Omit<FuturesAllocationInput, 'parentLocation'>, locationIds: number[]) {
    const { fiscalYearMonths, mappings, positions } = input;
    const futuresHere = positions.filter(p => locationIds.includes(p.location?.locationId));

    return contractMonthsFor(fiscalYearMonths, mappings, futuresHere).map(contractMonth => ({
        contractMonth,
        budgetMonths: fiscalYearMonths.filter(m => sameMonth(contractMonthForBudgetMonth(m, mappings), contractMonth)),
    }));
}

/**
 * One flat row per contract month for the parent location: futures bought there, futures
 * sold to roll volume down to a child, the net still held, and the parent's budget. Hedges
 * traded directly at a child flagged as having no exchange for physical are counted here.
 */
export function buildParentRows(input: FuturesAllocationInput): { rows: FuturesAllocationRow[]; total: FuturesAllocationRow } {
    const { parentLocation, childLocations, positions, budgets, mappings, locationIdsWithoutEfp } = input;
    const hedgeLocationIds = [parentLocation.locationId, ...rolledUpChildren(childLocations, locationIdsWithoutEfp).map(c => c.locationId)];
    const buckets = contractMonthBuckets(input, hedgeLocationIds);

    const rows = buckets.map<FuturesAllocationRow>(({ contractMonth, budgetMonths }) => ({
        path: [monthLabel(contractMonth)],
        monthSortKey: monthSortKey(contractMonth),
        locationName: parentLocation.locationName,
        ...volumesAt(positions, hedgeLocationIds, [parentLocation.locationId], contractMonth, budgetMonths, mappings),
        budgetVolume: parentBudget(budgets, parentLocation.locationId, childLocations, budgetMonths),
    }));

    return { rows, total: sumRows(['Total'], 0, rows) };
}

/**
 * Tree rows for the child locations: a row per child summing its fiscal year, with a
 * contract month row beneath it. Each child's futures should net to zero once an
 * exchange for physical has gone through, leaving its physical volume against its budget.
 * A child flagged as trading its own hedges shows them here too, so its card is the plant
 * level breakdown of what the parent table already counts.
 */
export function buildChildRows(input: Omit<FuturesAllocationInput, 'parentLocation'>): { rows: FuturesAllocationRow[]; total: FuturesAllocationRow } {
    const { childLocations, positions, budgets, mappings } = input;
    const buckets = contractMonthBuckets(input, childLocations.map(c => c.locationId));
    const rows: FuturesAllocationRow[] = [];
    const locationRows: FuturesAllocationRow[] = [];

    for (const child of childLocations) {
        const months = buckets.map<FuturesAllocationRow>(({ contractMonth, budgetMonths }) => ({
            path: [child.locationName, monthLabel(contractMonth)],
            monthSortKey: monthSortKey(contractMonth),
            locationName: child.locationName,
            ...volumesAt(positions, [child.locationId], [child.locationId], contractMonth, budgetMonths, mappings),
            budgetVolume: ownBudget(budgets, child.locationId, budgetMonths) ?? 0,
        }));

        const locationRow = sumRows([child.locationName], 0, months, child.locationName);
        locationRows.push(locationRow);
        rows.push(locationRow, ...months);
    }

    return { rows, total: sumRows(['Total'], 0, locationRows) };
}
